import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface DrawerModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  footer?: React.ReactNode;
  backgroundImage?: string;
}

export const DrawerModal: React.FC<DrawerModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = true,
  footer,
  backgroundImage = "/assets/images/bgillustra/fondvert.jpeg",
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;

  const getInitial = () => {
    if (isDesktop) return { x: '100%', opacity: 0 };
    return { y: '100%', opacity: 0 };
  };
  const getAnimate = () => {
    if (isDesktop) return { x: 0, opacity: 1 };
    return { y: 0, opacity: 1 };
  };
  const getExit = () => {
    if (isDesktop) return { x: '100%', opacity: 0 };
    return { y: '100%', opacity: 0 };
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-white/5 backdrop-blur-sm"
            onClick={onClose}
          />
          <div className="fixed inset-0 z-50 pointer-events-none flex">
            <div className={`pointer-events-auto w-full ${isDesktop ? 'ml-auto max-w-md h-full' : 'mt-auto max-h-[90vh]'}`}>
              <motion.div
                initial={getInitial()}
                animate={getAnimate()}
                exit={getExit()}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className={`
                  relative shadow-2xl flex flex-col w-full overflow-hidden
                  ${isDesktop 
                    ? 'h-full rounded-l-2xl' 
                    : 'rounded-t-2xl max-h-[90vh]'}
                `}
              >
                {/* Image de fond */}
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url('${backgroundImage}')` }}
                />
                {/* Overlay sombre pour la lisibilité */}
                <div className="absolute inset-0 bg-black/20" />

                {/* Contenu (relatif pour être au-dessus) */}
                <div className="relative z-10 flex flex-col h-full">
                  {/* Header avec fond semi-transparent */}
                  <div className={`
                    flex items-center justify-between p-5 border-b border-white/20 sticky top-0 z-10
                    ${isDesktop ? 'rounded-l-2xl' : 'rounded-t-2xl'}
                    bg-black/5 backdrop-blur-sm
                  `}>
                    <h2 className="text-xl font-semibold text-white">{title}</h2>
                    {showCloseButton && (
                      <button
                        onClick={onClose}
                        className="
                          flex items-center justify-center w-8 h-8 rounded-full 
                          bg-white/20 backdrop-blur-sm
                          hover:bg-white/40 transition-all duration-200
                          text-white
                        "
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  {/* Contenu principal */}
                  <div className="flex-1 overflow-y-auto p-5 text-white">
                    {children}
                  </div>

                  {/* Footer (si fourni) */}
                  {footer && (
                    <div className="sticky bottom-0 bg-black/30 backdrop-blur-sm border-t border-white/20 p-4 flex justify-end gap-3">
                      {footer}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};